import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Save, Loader2, Lock, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-gold/15 bg-obsidian px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-gold/60";

const formatDetail = (detail) => {
  if (detail == null) return "Bir hata oluştu.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const Field = ({ label, value, onChange, textarea, testid }) => (
  <div>
    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} rows={3} className={`${inputCls} resize-y`} value={value} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const ImageField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">{label}</label>
    <div className="flex items-center gap-3">
      {value && <img src={value} alt="" className="h-10 w-16 shrink-0 rounded border border-gold/20 object-cover" />}
      <input data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

const TABS = [
  { id: "hero", label: "Hero" },
  { id: "marquee", label: "Marquee" },
  { id: "manifesto", label: "Manifesto" },
  { id: "hizmetler", label: "Hizmetler" },
  { id: "isler", label: "İşler" },
  { id: "iletisim", label: "İletişim" },
  { id: "footer", label: "Footer" },
  { id: "talepler", label: "Talepler" },
];

function Leads() {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/leads`, { withCredentials: true })
      .then((r) => setLeads(r.data))
      .catch(() => setLeads([]));
  }, []);

  if (leads === null) return <Loader2 className="h-6 w-6 animate-spin text-gold" />;
  if (leads.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-zinc-500" data-testid="leads-empty">
        <Inbox className="h-8 w-8" />
        <p className="text-sm">Henüz talep yok.</p>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="leads-list">
      {leads.map((l) => (
        <div key={l.id} className="rounded-xl border border-gold/15 bg-obsidian p-4" data-testid={`lead-${l.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-display text-base font-bold text-zinc-100">{l.name}</p>
            <p className="font-mono text-[10px] text-zinc-500">{new Date(l.created_at).toLocaleString("tr-TR")}</p>
          </div>
          <p className="mt-1 text-sm text-gold">{l.phone}{l.email ? ` • ${l.email}` : ""}</p>
          {l.service && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l.service}</p>}
          {l.message && <p className="mt-2 text-sm text-zinc-400">{l.message}</p>}
        </div>
      ))}
    </div>
  );
}

function Editor({ user, onLogout }) {
  const [tab, setTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch(() => toast.error("İçerik yüklenemedi."));
  }, []);

  const setPath = (path, value) =>
    setContent((c) => {
      const copy = JSON.parse(JSON.stringify(c));
      let node = copy;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return copy;
    });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, { withCredentials: true });
      toast.success("İçerik kaydedildi. Sitede anında yayında.");
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Oturum süresi doldu. Tekrar giriş yapın.");
        onLogout();
      } else {
        toast.error(formatDetail(e.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  if (!content)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Reya Desing</p>
          <h1 className="font-display text-2xl font-extrabold text-zinc-100">İçerik Paneli</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-zinc-500 sm:block">{user.email}</span>
          <button
            data-testid="admin-save-btn"
            onClick={save}
            disabled={saving || tab === "talepler"}
            className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-display text-sm font-bold text-obsidian transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="flex items-center gap-2 rounded-full border border-gold/25 px-4 py-2.5 text-sm text-zinc-300 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <LogOut className="h-4 w-4" />
            Çıkış
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
              tab === t.id ? "bg-gold text-obsidian" : "border border-gold/20 text-zinc-400 hover:text-gold"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-5 rounded-2xl border border-gold/15 bg-obsidian-800 p-6 sm:p-8">
        {tab === "hero" && (
          <>
            <Field label="Üst Etiket" value={content.hero.overline} onChange={(v) => setPath(["hero", "overline"], v)} testid="edit-hero-overline" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Başlık Satır 1" value={content.hero.line1} onChange={(v) => setPath(["hero", "line1"], v)} testid="edit-hero-line1" />
              <Field label="Başlık Satır 2 (altın)" value={content.hero.line2} onChange={(v) => setPath(["hero", "line2"], v)} testid="edit-hero-line2" />
              <Field label="Başlık Satır 3" value={content.hero.line3} onChange={(v) => setPath(["hero", "line3"], v)} testid="edit-hero-line3" />
            </div>
            <Field label="Alt Açıklama" textarea value={content.hero.subtitle} onChange={(v) => setPath(["hero", "subtitle"], v)} testid="edit-hero-subtitle" />
            <ImageField label="Görsel 1 (Tabela)" value={content.hero.images.main} onChange={(v) => setPath(["hero", "images", "main"], v)} testid="edit-hero-img-main" />
            <ImageField label="Görsel 2 (Matbaa)" value={content.hero.images.print} onChange={(v) => setPath(["hero", "images", "print"], v)} testid="edit-hero-img-print" />
            <ImageField label="Görsel 3 (Prodüksiyon)" value={content.hero.images.video} onChange={(v) => setPath(["hero", "images", "video"], v)} testid="edit-hero-img-video" />
          </>
        )}

        {tab === "marquee" && (
          <Field
            label="Kayan Bant Öğeleri (her satıra bir öğe)"
            textarea
            value={content.marquee.join("\n")}
            onChange={(v) => setPath(["marquee"], v.split("\n").map((s) => s.trim()).filter(Boolean))}
            testid="edit-marquee-items"
          />
        )}

        {tab === "manifesto" && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Başlık (baş)" value={content.manifesto.titlePre} onChange={(v) => setPath(["manifesto", "titlePre"], v)} testid="edit-manifesto-titlepre" />
              <Field label="Başlık (altın)" value={content.manifesto.titleGold} onChange={(v) => setPath(["manifesto", "titleGold"], v)} testid="edit-manifesto-titlegold" />
              <Field label="Başlık (son)" value={content.manifesto.titlePost} onChange={(v) => setPath(["manifesto", "titlePost"], v)} testid="edit-manifesto-titlepost" />
            </div>
            {content.manifesto.chapters.map((ch, i) => (
              <div key={ch.no} className="rounded-xl border border-gold/10 p-4">
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Bölüm {ch.no}</p>
                <div className="space-y-3">
                  <Field label="Başlık" value={ch.title} onChange={(v) => setPath(["manifesto", "chapters", i, "title"], v)} testid={`edit-manifesto-ch${i}-title`} />
                  <Field label="Metin" textarea value={ch.copy} onChange={(v) => setPath(["manifesto", "chapters", i, "copy"], v)} testid={`edit-manifesto-ch${i}-copy`} />
                </div>
              </div>
            ))}
          </>
        )}

        {tab === "hizmetler" && (
          <>
            <Field label="Bölüm Başlığı (baş)" value={content.services.titlePre} onChange={(v) => setPath(["services", "titlePre"], v)} testid="edit-services-titlepre" />
            <Field label="Bölüm Başlığı (altın)" value={content.services.titleGold} onChange={(v) => setPath(["services", "titleGold"], v)} testid="edit-services-titlegold" />
            <Field label="Tanıtım Metni" textarea value={content.services.intro} onChange={(v) => setPath(["services", "intro"], v)} testid="edit-services-intro" />
            {content.services.categories.map((cat, ci) => (
              <details key={cat.id} className="rounded-xl border border-gold/10 p-4" data-testid={`edit-category-${cat.id}`}>
                <summary className="cursor-pointer font-display text-base font-bold text-zinc-100">
                  {cat.number} — {cat.title}
                </summary>
                <div className="mt-4 space-y-3">
                  <Field label="Kategori Başlığı" value={cat.title} onChange={(v) => setPath(["services", "categories", ci, "title"], v)} testid={`edit-cat-${cat.id}-title`} />
                  <Field label="Kategori Sloganı" value={cat.tagline} onChange={(v) => setPath(["services", "categories", ci, "tagline"], v)} testid={`edit-cat-${cat.id}-tagline`} />
                  {cat.services.map((s, si) => (
                    <div key={si} className="rounded-lg border border-gold/5 bg-obsidian p-3">
                      <div className="space-y-3">
                        <Field label={`Hizmet ${si + 1} — Ad`} value={s.name} onChange={(v) => setPath(["services", "categories", ci, "services", si, "name"], v)} testid={`edit-cat-${cat.id}-svc-${si}-name`} />
                        <Field label="Açıklama" textarea value={s.desc} onChange={(v) => setPath(["services", "categories", ci, "services", si, "desc"], v)} testid={`edit-cat-${cat.id}-svc-${si}-desc`} />
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </>
        )}

        {tab === "isler" && (
          <>
            <Field label="Bölüm Başlığı (baş)" value={content.showcase.titlePre} onChange={(v) => setPath(["showcase", "titlePre"], v)} testid="edit-showcase-titlepre" />
            <Field label="Bölüm Başlığı (altın)" value={content.showcase.titleGold} onChange={(v) => setPath(["showcase", "titleGold"], v)} testid="edit-showcase-titlegold" />
            {content.showcase.tiles.map((t, i) => (
              <div key={i} className="space-y-3 rounded-xl border border-gold/10 p-4">
                <Field label={`Görsel ${i + 1} — Etiket`} value={t.label} onChange={(v) => setPath(["showcase", "tiles", i, "label"], v)} testid={`edit-tile-${i}-label`} />
                <ImageField label="Görsel URL" value={t.src} onChange={(v) => setPath(["showcase", "tiles", i, "src"], v)} testid={`edit-tile-${i}-src`} />
              </div>
            ))}
          </>
        )}

        {tab === "iletisim" && (
          <>
            <Field label="Tanıtım Metni" textarea value={content.contact.intro} onChange={(v) => setPath(["contact", "intro"], v)} testid="edit-contact-intro" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Telefon (görünen)" value={content.contact.phone} onChange={(v) => setPath(["contact", "phone"], v)} testid="edit-contact-phone" />
              <Field label="WhatsApp (ülke koduyla, örn: 905458276105)" value={content.contact.whatsapp} onChange={(v) => setPath(["contact", "whatsapp"], v)} testid="edit-contact-whatsapp" />
              <Field label="E-posta" value={content.contact.email} onChange={(v) => setPath(["contact", "email"], v)} testid="edit-contact-email" />
              <Field label="Adres / Konum" value={content.contact.address} onChange={(v) => setPath(["contact", "address"], v)} testid="edit-contact-address" />
            </div>
          </>
        )}

        {tab === "footer" && (
          <>
            <Field label="Slogan" value={content.footer.tagline} onChange={(v) => setPath(["footer", "tagline"], v)} testid="edit-footer-tagline" />
            <Field label="Telif Metni" value={content.footer.copyright} onChange={(v) => setPath(["footer", "copyright"], v)} testid="edit-footer-copyright" />
            <Field label="Alt Etiket" value={content.footer.meta} onChange={(v) => setPath(["footer", "meta"], v)} testid="edit-footer-meta" />
          </>
        )}

        {tab === "talepler" && <Leads />}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  };

  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );

  if (!user)
    return (
      <div className="flex min-h-screen items-center justify-center px-4" data-testid="admin-login">
        <form
          onSubmit={login}
          className="glass-panel gold-glow w-full max-w-sm rounded-3xl p-8 sm:p-10"
          data-testid="admin-login-form"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
            <Lock className="h-6 w-6 text-gold" />
          </div>
          <h1 className="mt-6 text-center font-display text-2xl font-extrabold text-zinc-100">
            REYA<span className="text-gold">©</span>DESING
          </h1>
          <p className="mt-1 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Yönetim Paneli</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">E-posta</label>
              <input
                data-testid="admin-email-input"
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@reyadesing.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-gold/80">Şifre</label>
              <input
                data-testid="admin-password-input"
                type="password"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-display text-sm font-bold text-obsidian transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş Yap"}
            </button>
          </div>
        </form>
      </div>
    );

  return <Editor user={user} onLogout={logout} />;
}
